import * as React from 'react';
import { EnvKey } from '..';
import { HttpErrorModel } from '../models/http-error';
import { EnvValue, useEnv } from './env';
import {
  HookHttpConfigInterface,
  RequestParams,
  ResponseWithHeaders,
  useHttp,
} from './http';

function sanitizeBackendUrl(backendUrl: string): string {
  return backendUrl.trim().replace(/\/$/, '');
}

function sanitizeEndpoint(endpoint: string): string {
  return endpoint.trim().replace(/^\//, '');
}

function sanitize(endpoint: string, forceBackendUrl?: string): string {
  let url = sanitizeEndpoint(endpoint);
  if (forceBackendUrl) {
    url = `${sanitizeBackendUrl(forceBackendUrl)}/${url}`;
  }
  return url;
}

export interface HookBackendInterface {
  backendGet<Response>(
    endpoint: string,
    params?: RequestParams,
  ): Promise<Response>;
  backendGetWithHeaders<Data>(
    endpoint: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  backendGetText(endpoint: string, params?: RequestParams): Promise<string>;
  backendGetTextWithHeaders(
    endpoint: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<string>>;
  backendGetBlob(endpoint: string, params?: RequestParams): Promise<Blob>;
  backendGetBlobWithHeaders(
    endpoint: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Blob>>;
  backendDelete<Response>(
    endpoint: string,
    params?: RequestParams,
  ): Promise<Response>;
  backendDeleteWithHeaders<Data>(
    endpoint: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  backendPost<Response, Payload = unknown>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  backendPostWithHeaders<Data, Payload = unknown>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  backendPostBlob<Payload>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Blob>;
  backendPostBlobWithHeaders<Payload>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Blob>>;
  backendPut<Response, Payload = unknown>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  backendPutWithHeaders<Data, Payload = unknown>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  backendPatch<Response, Payload = unknown>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  backendPatchWithHeaders<Data, Payload = unknown>(
    endpoint: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
}

export interface HookBackendConfigInterface
  extends Partial<Omit<HookHttpConfigInterface, 'baseUrl' | 'injectToken'>> {
  backendUrlEnvKey?: EnvKey;
}

const backendUrlEnvKey = 'backendUrl';
const injectToken = true;

export function useBackend(
  config: HookBackendConfigInterface = {},
): HookBackendInterface {
  const { getEnv } = useEnv();

  const [cachedBackendUrl, setCachedBackendUrl] = React.useState<EnvValue>();

  const httpConfig = React.useMemo(
    () => ({
      injectToken,
      baseUrl: cachedBackendUrl as string,
      authBasicCredentials: config.authBasicCredentials,
      headers: config.headers,
      proxyConfig: config.proxyConfig,
      withCredentials: config.withCredentials,
    }),
    [
      cachedBackendUrl,
      config.authBasicCredentials,
      config.headers,
      config.proxyConfig,
      config.withCredentials,
    ],
  );

  const {
    httpGet,
    httpGetWithHeaders,
    httpGetText,
    httpGetTextWithHeaders,
    httpGetBlob,
    httpGetBlobWithHeaders,
    httpDelete,
    httpDeleteWithHeaders,
    httpPost,
    httpPostWithHeaders,
    httpPostBlob,
    httpPostBlobWithHeaders,
    httpPut,
    httpPutWithHeaders,
    httpPatch,
    httpPatchWithHeaders,
  } = useHttp(httpConfig);

  React.useEffect(() => {
    void getEnv(backendUrlEnvKey).then(setCachedBackendUrl);
  }, [getEnv]);

  const request = React.useCallback(
    async <Response, Payload = never>({
      method,
      endpoint,
      params = new Map(),
      body = {} as Payload,
      responseType = 'json',
      responseWithHeaders = false,
    }: {
      method: 'GET' | 'DELETE' | 'POST' | 'PUT' | 'PATCH';
      endpoint: string;
      params?: RequestParams;
      body?: Payload;
      responseType?: 'json' | 'text' | 'blob';
      responseWithHeaders?: boolean;
    }): Promise<Response> => {
      const getters = {
        json: httpGet,
        text: httpGetText,
        blob: httpGetBlob,
      };

      const posters = {
        json: httpPost as <Response, Payload>(
          url: string,
          body?: Payload,
          params?: RequestParams,
        ) => Promise<Response>,
        blob: httpPostBlob as <Blob, Payload>(
          url: string,
          body?: Payload,
          params?: RequestParams,
        ) => Promise<Blob>,
      };

      const gettersWithHeaders = {
        json: httpGetWithHeaders,
        text: httpGetTextWithHeaders,
        blob: httpGetBlobWithHeaders,
      };

      const postersWithHeaders = {
        json: httpPostWithHeaders as <Data, Payload>(
          url: string,
          body?: Payload,
          params?: RequestParams,
        ) => Promise<ResponseWithHeaders<Data>>,
        blob: httpPostBlobWithHeaders as <Blob, Payload>(
          url: string,
          body?: Payload,
          params?: RequestParams,
        ) => Promise<ResponseWithHeaders<Blob>>,
      };

      const methods = {
        GET: (nonCachedBackendUrl?: string) =>
          responseWithHeaders
            ? gettersWithHeaders[responseType](
                sanitize(endpoint, nonCachedBackendUrl),
                params,
              )
            : getters[responseType](
                sanitize(endpoint, nonCachedBackendUrl),
                params,
              ),
        DELETE: (nonCachedBackendUrl?: string) =>
          responseWithHeaders
            ? httpDeleteWithHeaders<Response>(
                sanitize(endpoint, nonCachedBackendUrl),
                params,
              )
            : httpDelete<Response>(
                sanitize(endpoint, nonCachedBackendUrl),
                params,
              ),
        POST: (nonCachedBackendUrl?: string) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          responseWithHeaders
            ? postersWithHeaders[
                responseType as keyof typeof postersWithHeaders
              ](sanitize(endpoint, nonCachedBackendUrl), body, params)
            : posters[responseType as keyof typeof posters](
                sanitize(endpoint, nonCachedBackendUrl),
                body,
                params,
              ),
        PUT: (nonCachedBackendUrl?: string) =>
          responseWithHeaders
            ? httpPutWithHeaders<Response, Payload>(
                sanitize(endpoint, nonCachedBackendUrl),
                body,
                params,
              )
            : httpPut<Response, Payload>(
                sanitize(endpoint, nonCachedBackendUrl),
                body,
                params,
              ),
        PATCH: (nonCachedBackendUrl?: string) =>
          responseWithHeaders
            ? httpPatchWithHeaders<Response, Payload>(
                sanitize(endpoint, nonCachedBackendUrl),
                body,
                params,
              )
            : httpPatch<Response, Payload>(
                sanitize(endpoint, nonCachedBackendUrl),
                body,
                params,
              ),
      };

      const methodResponse = {
        GET: ['json', 'text', 'blob'],
        DELETE: ['json'],
        POST: ['json', 'blob'],
        PUT: ['json'],
        PATCH: ['json'],
      };

      if (
        !methodResponse[method]?.find((response) => response === responseType)
      ) {
        return Promise.reject(
          new HttpErrorModel('Requisição inválida', 0, 'core.request.invalid'),
        );
      }

      if (cachedBackendUrl) {
        return methods[method](cachedBackendUrl as string) as Promise<Response>;
      }

      const backendUrl = await getEnv(backendUrlEnvKey);
      if (!backendUrl) {
        return Promise.reject(
          new HttpErrorModel(
            'URL do backend não informada',
            0,
            'core.backendUrl.notFound',
          ),
        );
      }
      return methods[method](backendUrl as string) as Promise<Response>;
    },
    [
      cachedBackendUrl,
      getEnv,
      httpDelete,
      httpDeleteWithHeaders,
      httpGet,
      httpGetBlob,
      httpGetBlobWithHeaders,
      httpGetText,
      httpGetTextWithHeaders,
      httpGetWithHeaders,
      httpPatch,
      httpPatchWithHeaders,
      httpPost,
      httpPostBlob,
      httpPostBlobWithHeaders,
      httpPostWithHeaders,
      httpPut,
      httpPutWithHeaders,
    ],
  );

  const backendGet = React.useCallback(
    <Response>(endpoint: string, params: RequestParams = new Map()) =>
      request<Response>({ method: 'GET', endpoint, params }),
    [request],
  );

  const backendGetWithHeaders = React.useCallback(
    <Data>(endpoint: string, params: RequestParams = new Map()) =>
      request<ResponseWithHeaders<Data>>({
        method: 'GET',
        endpoint,
        params,
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendGetText = React.useCallback(
    (endpoint: string, params: RequestParams = new Map()) =>
      request<string>({
        method: 'GET',
        endpoint,
        params,
        responseType: 'text',
      }),
    [request],
  );

  const backendGetTextWithHeaders = React.useCallback(
    (endpoint: string, params: RequestParams = new Map()) =>
      request<ResponseWithHeaders<string>>({
        method: 'GET',
        endpoint,
        params,
        responseType: 'text',
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendGetBlob = React.useCallback(
    <Blob>(endpoint: string, params: RequestParams = new Map()) =>
      request<Blob>({
        method: 'GET',
        endpoint,
        params,
        responseType: 'blob',
      }),
    [request],
  );

  const backendGetBlobWithHeaders = React.useCallback(
    <Blob>(endpoint: string, params: RequestParams = new Map()) =>
      request<ResponseWithHeaders<Blob>>({
        method: 'GET',
        endpoint,
        params,
        responseType: 'blob',
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendDelete = React.useCallback(
    <Response>(endpoint: string, params: RequestParams = new Map()) =>
      request<Response>({ method: 'DELETE', endpoint, params }),
    [request],
  );

  const backendDeleteWithHeaders = React.useCallback(
    <Data>(endpoint: string, params: RequestParams = new Map()) =>
      request<ResponseWithHeaders<Data>>({
        method: 'DELETE',
        endpoint,
        params,
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendPost = React.useCallback(
    <Response, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => request<Response, Payload>({ method: 'POST', endpoint, params, body }),
    [request],
  );

  const backendPostWithHeaders = React.useCallback(
    <Data, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) =>
      request<ResponseWithHeaders<Data>, Payload>({
        method: 'POST',
        endpoint,
        params,
        body,
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendPostBlob = React.useCallback(
    <Blob, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) =>
      request<Blob, Payload>({
        method: 'POST',
        endpoint,
        params,
        body,
        responseType: 'blob',
      }),
    [request],
  );

  const backendPostBlobWithHeaders = React.useCallback(
    <Blob, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) =>
      request<ResponseWithHeaders<Blob>, Payload>({
        method: 'POST',
        endpoint,
        params,
        body,
        responseType: 'blob',
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendPut = React.useCallback(
    <Response, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => request<Response, Payload>({ method: 'PUT', endpoint, params, body }),
    [request],
  );

  const backendPutWithHeaders = React.useCallback(
    <Data, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) =>
      request<ResponseWithHeaders<Data>, Payload>({
        method: 'PUT',
        endpoint,
        params,
        body,
        responseWithHeaders: true,
      }),
    [request],
  );

  const backendPatch = React.useCallback(
    <Response, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) =>
      request<Response, Payload>({ method: 'PATCH', endpoint, params, body }),
    [request],
  );

  const backendPatchWithHeaders = React.useCallback(
    <Data, Payload>(
      endpoint: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) =>
      request<ResponseWithHeaders<Data>, Payload>({
        method: 'PATCH',
        endpoint,
        params,
        body,
        responseWithHeaders: true,
      }),
    [request],
  );

  return {
    backendGet,
    backendGetWithHeaders,
    backendGetText,
    backendGetTextWithHeaders,
    backendGetBlob,
    backendGetBlobWithHeaders,
    backendDelete,
    backendDeleteWithHeaders,
    backendPost,
    backendPostWithHeaders,
    backendPostBlob,
    backendPostBlobWithHeaders,
    backendPut,
    backendPutWithHeaders,
    backendPatch,
    backendPatchWithHeaders,
  };
}
